"use client";

import Image from "next/image";
import { useTranslation } from "react-i18next";
import Link from "@/app/components/LocalizedLink";
import { useLocalizedRouter } from "@/app/hooks/useLocalizedRouter";
import { trackAutoBuilderFormSubmit } from "@/app/lib/tracking";
import { createLogger } from "@/app/lib/logger";
import { CAMPAIGN_ALT_ICON } from "../animatedIcons";
import CompanyLinkInput from "../CompanyLinkInput";
import Reveal from "./Reveal";
import TiltCard from "./TiltCard";
import { CheckMark } from "./BusinessMarks";

const log = createLogger("FinalSignal");

export const DISTRIBUTION_CHANNELS: { key: string; icon?: string }[] = [
  { key: "facebook", icon: "/images/Facebook.png" },
  { key: "instagram", icon: "/images/Insta.png" },
  { key: "linkedin", icon: "/images/Linkedin.png" },
  { key: "googleJobs", icon: "/images/Google.png" },
  { key: "tiktokReels" },
  { key: "careerPage" },
];

/** Splits "A • B • C" style trust lines into separate items. */
export function splitTrustLine(line: string): string[] {
  return line
    .split(/\s*[•·|]\s*/)
    .map(part => part.trim())
    .filter(Boolean);
}

export default function FinalSignal() {
  const { t } = useTranslation();
  const router = useLocalizedRouter();

  const handleFormSubmit = async (companyUrl: string) => {
    sessionStorage.setItem("companyUrl", companyUrl);
    sessionStorage.setItem("hasPendingJob", "true");

    trackAutoBuilderFormSubmit({
      company_url: companyUrl,
      trigger_source: "final_cta",
    }).catch((error: unknown) => {
      log.error("Failed to track AutoBuilder form submit", error, {
        trigger_source: "final_cta",
      });
    });

    router.push("/register");
  };

  const trustItems = splitTrustLine(t("landing.finalCta.trustLine"));

  return (
    <section
      aria-labelledby="final-cta-title"
      className="relative overflow-hidden text-signal-ink"
    >
      <div className="relative mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:items-center lg:gap-16 lg:px-8 lg:py-28">
        <Reveal className="lg:col-span-6">
          <h2
            id="final-cta-title"
            className="max-w-[18ch] text-balance font-display text-[clamp(2.25rem,4.4vw,3.75rem)] font-semibold leading-[1.04] tracking-[-0.04em] text-signal-ink"
          >
            {t("landing.finalCta.headline")}
          </h2>
          <p className="mt-5 max-w-[46ch] text-pretty text-[18px] leading-[1.6] text-signal-muted">
            {t("landing.finalCta.subheadline")}
          </p>
          <CompanyLinkInput
            variant="bare"
            ctaIcon={CAMPAIGN_ALT_ICON}
            onSubmit={handleFormSubmit}
            className="mt-8 max-w-[520px]"
          />
          {trustItems.length > 0 && (
            <ul className="mt-6 flex flex-wrap gap-2.5">
              {trustItems.map(item => (
                <li
                  key={item}
                  className="glass-pill flex items-center gap-2 py-2 pl-2.5 pr-4 text-[13px] font-medium text-signal-ink"
                >
                  <CheckMark className="h-4 w-4 text-signal-accent" />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </Reveal>

        {/* Where the ad lands: a dome of channels, each riding its own disc */}
        <Reveal delay={150} className="lg:col-span-6">
          <TiltCard
            restX={2}
            restY={-4}
            strength={4}
            lift={16}
            className="glass-flat rounded-orbitDome px-7 pb-9 pt-10 sm:px-9"
          >
            <h3 className="text-center text-[15px] font-semibold text-signal-ink">
              {t("landing.whatYouGet.distributionTitle")}
            </h3>
            <ul className="mt-7 flex flex-wrap justify-center gap-3">
              {DISTRIBUTION_CHANNELS.map((channel, index) => (
                <li
                  key={channel.key}
                  className="glass-flat flex items-center gap-2.5 rounded-full py-2 pl-2 pr-5 text-[15px] font-semibold text-signal-ink animate-orbit-float motion-reduce:animate-none"
                  style={{ animationDelay: `${index * 450}ms` }}
                >
                  {channel.icon ? (
                    <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full shadow-orbitAvatar">
                      <Image
                        src={channel.icon}
                        alt=""
                        fill
                        sizes="32px"
                        className="object-cover"
                      />
                    </span>
                  ) : (
                    <span className="btn-glass-primary h-8 w-8 shrink-0">
                      <CheckMark className="h-5 w-5" />
                    </span>
                  )}
                  {t(`landing.whatYouGet.channels.${channel.key}`)}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex justify-center">
              <Link
                href="/blog"
                className="btn-glass-ghost h-11 px-6 text-[15px] text-signal-deep"
              >
                {t("landing.finalCta.secondaryButton")}
              </Link>
            </div>
          </TiltCard>
        </Reveal>
      </div>
    </section>
  );
}
